import type { ReactNode } from 'react'
import type { TimeOfDay } from '@/shared/timeOfDay'
import { Sky } from '@/shared/ui/Sky'

export function Hero({
  tod,
  eyebrow,
  title,
  summary,
  actions,
  focus,
}: {
  tod: TimeOfDay
  eyebrow: ReactNode
  title: ReactNode
  summary: ReactNode
  actions: ReactNode
  focus: ReactNode
}) {
  return (
    // The shared sky, with Beranda's faint grid; login draws the same sky plain.
    <Sky tod={tod} grid className="pt-10 pb-24">
      <div className="mx-auto grid max-w-6xl items-center gap-8 px-4 sm:px-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        <div className="animate-rise">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/15 py-1 pr-2.5 pl-2 text-[0.8rem] font-medium">
            <span className="size-[7px] rounded-full bg-[#7dffb4] shadow-[0_0_0_3px_rgba(125,255,180,0.25)]" />
            {eyebrow}
          </span>
          <h1 className="mt-4 text-[clamp(1.9rem,4.6vw,2.9rem)] leading-[1.08] font-extrabold tracking-tight text-balance text-white">
            {title}
          </h1>
          <p className="mt-3 max-w-[52ch] text-base leading-relaxed text-white/85 [&_b]:font-semibold [&_b]:text-white">
            {summary}
          </p>
          <div className="mt-5 flex flex-wrap gap-2.5">{actions}</div>
        </div>
        {focus && (
          <div className="animate-rise [animation-delay:80ms]">{focus}</div>
        )}
      </div>
    </Sky>
  )
}

/** The frosted panel on the right of the hero. */
export function FocusPanel({ label, icon, children }: { label: string; icon: ReactNode; children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-white/25 bg-white/12 p-5 shadow-[0_20px_40px_-20px_rgba(0,0,0,0.4)] backdrop-blur-md">
      <div className="flex items-center gap-1.5 text-[0.72rem] font-semibold tracking-[0.08em] text-white/70 uppercase">
        {icon}
        {label}
      </div>
      {children}
    </div>
  )
}

export const HERO_BUTTON = {
  white:
    'inline-flex h-[2.625rem] items-center gap-2 rounded-lg bg-white px-4.5 font-semibold text-[#0b1f4d] shadow-[0_8px_20px_-8px_rgba(0,0,0,0.35)] transition hover:-translate-y-px hover:bg-[#f2f9ff] hover:text-[#0b1f4d]',
  ghost:
    'inline-flex h-[2.625rem] items-center gap-2 rounded-lg border border-white/30 bg-white/12 px-4.5 font-semibold text-white transition hover:-translate-y-px hover:bg-white/20 hover:text-white',
}
